<template>
  <vs-dropdown vs-custom-content vs-trigger-click class="notifications-bell">
    <div class="bell-trigger cursor-pointer flex items-center">
      <feather-icon icon="BellIcon" svgClasses="w-5 h-5" />
      <span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>
    </div>

    <vs-dropdown-menu class="notifications-menu">
      <div class="menu-header">
        <div class="title">
          <h4>Notificações</h4>
          <span class="counter">{{ unreadCount }} não lidas</span>
        </div>
        <div class="header-actions">
          <vs-button size="small" type="line" icon-pack="feather" icon="icon-rotate-ccw" @click="refresh" />
          <vs-button size="small" @click="markAllRead" :disabled="!notifications.length">Marcar lidas</vs-button>
        </div>
      </div>

      <div v-if="!notifications.length" class="empty">
        Nenhuma notificação encontrada.
      </div>

      <div v-else class="list">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-card"
          :class="{'unread': !notification.is_read}"
        >
          <div class="card-top">
            <div class="text">
              <p class="message">{{ notification.message }}</p>
              <small class="timestamp">{{ formatDate(notification.created_at).full }}</small>
            </div>
          </div>
          <div class="card-actions">
            <vs-button v-if="!notification.is_read" size="small" type="flat" @click="markOne(notification.id)">Marcar como lida</vs-button>
            <vs-button size="small" type="line" @click="goToRequest(notification.request_id)" v-if="notification.request_id">Ver</vs-button>
          </div>
        </div>
      </div>
    </vs-dropdown-menu>
  </vs-dropdown>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils'

export default {
  name: 'NotificationsBell',
  computed: {
    ...mapGetters('approvals', ['notifications', 'unreadCount'])
  },
  methods: {
    formatDate,
    async refresh() {
      await this.$store.dispatch('approvals/fetchNotifications', { limit: 25 }).catch(() => {});
    },
    async markOne(id) {
      await this.$store.dispatch('approvals/markNotificationsRead', { notification_ids: [id] });
      this.$vs.notify({ title: 'Notificação', text: 'Marcada como lida', color: 'success' });
    },
    async markAllRead() {
      if (!this.notifications.length) return;
      await this.$store.dispatch('approvals/markNotificationsRead', { mark_all: true });
      this.$vs.notify({ title: 'Notificações', text: 'Todas marcadas como lidas', color: 'success' });
    },
    async goToRequest(id) {
      if (!id) return;
      const target = `/approvals/${id}`;
      await this.$store.dispatch('approvals/fetchRequestById', id).catch(() => {});
      if (this.$route.path === target) {
        this.$router.replace({ path: target }).catch(() => {});
        return;
      }
      this.$router.push(target).catch(() => {});
    }
  },
  async created() {
    await this.refresh();
  }
}
</script>

<style scoped>
.notifications-bell .badge {
  background: #ff9f43;
  color: #1f2d3d;
  border-radius: 999px;
  font-size: 11px;
  padding: 2px 6px;
  margin-left: 6px;
  line-height: 1;
}

.notifications-menu {
  width: clamp(18rem, 80vw, 23rem);
  max-width: calc(100vw - 16px);
  background: #fff;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

.notifications-bell ::v-deep .vs-dropdown--menu {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  outline: none !important;
  display: block !important;
  max-width: 100vw !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
  max-height: 100vh !important;
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.title h4 {
  margin: 0;
  font-weight: 800;
  color: #0f172a;
}

.counter {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #0f172a;
  background: #fef3c7;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-actions .link {
  color: #2563eb;
  font-weight: 600;
}

.list {
  flex: 1 1 auto;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding-right: 6px;
}

.list::-webkit-scrollbar {
  width: 6px;
}

.list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.notification-card {
  padding: 14px 14px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 12px;
  background: #fff;
  transition: box-shadow 0.2s ease, transform 0.1s ease;
}

.notification-card.unread {
  background: linear-gradient(180deg, #fff9f0 0%, #fffdf8 100%);
  border-color: #ffd7a8;
}

.notification-card:hover {
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.message {
  margin: 0;
  font-size: 13px;
  color: #0f172a;
  line-height: 1.45;
  word-break: break-word;
}

.timestamp {
  color: #6b7280;
  font-size: 12px;
}

.badge-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  background: #f97316;
  border-radius: 50%;
  margin-left: 6px;
  flex-shrink: 0;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: nowrap;
}

.card-actions ::v-deep button {
  min-width: auto;
  padding: 6px 10px;
  height: auto;
  font-size: 12px;
}

.empty {
  text-align: center;
  color: #6b7280;
  padding: 20px 0;
}

.menu-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  border-top: 1px solid #eef2f7;
  padding-top: 10px;
}
</style>
